import { useState, useMemo, useCallback, useEffect } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Grid, Divider } from '@mui/material';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { InvoiceStatusEnum } from 'src/shared/models/enums/invoiceStatusEnum';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceSearchFilter } from '../models/invoiceSearchFilter';
import InvoiceDataGrid from './invoiceDataGrid';
import InvoiceTabs from './invoiceTabs';
import { Invoice } from '../models/invoice';

type IncomingBankPaymentEditOcrProps = {
  newOcrCallback: (ocr: Invoice) => void;
};

export const IncomingBankPaymentEditOcr = (props: IncomingBankPaymentEditOcrProps) => {
  const { newOcrCallback } = props;

  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  // const [searchFilterInitialized, setSearchFilterInitialized] = useState<boolean>(false); // Om avancerat sök ska in
  const [invoiceSearchFilter, setInvoiceSearchFilter] = useState<InvoiceSearchFilter>({
    status: {
      label: 'invoice:invoiceStatus.UnPaid',
      key: 'UnPaid',
      values: [InvoiceStatusEnum.UnPaid],
    },
  } as unknown as InvoiceSearchFilter);

  const searchFilter: InvoiceSearchFilter = useMemo(() => {
    return {
      ownerId: invoiceSearchFilter?.ownerId,
      invoiceMissionId: invoiceSearchFilter?.invoiceMissionId,
      searchString: invoiceSearchFilter?.searchString,
      status: invoiceSearchFilter?.status,
      pageIndex: invoiceSearchFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
      pageSize: invoiceSearchFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
    } as unknown as InvoiceSearchFilter;
  }, [
    invoiceSearchFilter?.invoiceMissionId,
    invoiceSearchFilter?.ownerId,
    invoiceSearchFilter?.pageIndex,
    invoiceSearchFilter?.pageSize,
    invoiceSearchFilter?.searchString,
    invoiceSearchFilter?.status,
  ]);

  const loadingCallback = useCallback(
    (isLoading: boolean) => {
      if (isMounted()) {
        setLoading(isLoading);
      }
    },
    [isMounted],
  );

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setInvoiceSearchFilter, tempSearchString]);

  const handleOcrChange = (ocr: Invoice) => {
    ConsoleHelper.log('handleOcrChange', ocr);
    newOcrCallback(ocr);
  };

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <Divider />
        <PageFilterSection
          leftContent={
            <SearchField
              initialValue={searchFilter.searchString || ''}
              onSearch={(newSearchString) => {
                setSearchInitialized(true);
                setTempSearchString(newSearchString);
              }}
            />
          }
        />
        <InvoiceTabs
          initialValue={searchFilter.status || undefined}
          disableAll={loading}
          callback={(val) => setInvoiceSearchFilter((prev) => ({ ...prev, ...{ status: val, pageIndex: 0 } }))}
        />
        <Divider />
        <InvoiceDataGrid
          loadingCallback={loadingCallback}
          searchFilter={searchFilter}
          hideCheckBoxSelection
          changeOcrMode
          changeOcrCallback={(x) => handleOcrChange(x)}
          searchFilterCallback={(newFilter) => {
            setInvoiceSearchFilter((prev) => ({
              ...prev,
              ...newFilter,
            }));
          }}
        />
      </Grid>
    </GridWrapper>
  );
};
